/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-export/QaItpExportView.tsx
 * FILE_ID: JOBHUB-QA-ITP-EXPORT-VIEW-001
 * PURPOSE: Full-screen ITP Export: edit, print, download, sign and send.
 */
import { Suspense, lazy, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, Download, FolderUp, Loader2, Printer, Save, Send } from 'lucide-react'
import { useIsMdUp } from '@quotech/ui'
import type { ItpCoverFields } from '../../../qa/itpCover'
import type { QaApi, QaAreaExecution } from '../../../qa/qaApi'
import type { QaItpRequest } from '../qaItpScope'
import { QaItpExportDocument } from './QaItpExportDocument'
import { QaItpHoldSignDialog } from './QaItpHoldSignDialog'
import { QaItpExportSide } from './QaItpExportSide'
import { QaItpSizeDialog } from './QaItpSizeDialog'
import { useQaItpExport } from './useQaItpExport'
import { QaItpSignMenu } from '../itp-sign/QaItpSignMenu'
import { QaItpSendCopyDialog } from '../itp-sign/QaItpSendCopyDialog'
import { QaItpSendConfirmDialog } from './QaItpSendConfirmDialog'
import { attachmentNameForCopy, useQaItpInlineSign } from '../itp-sign/useQaItpInlineSign'

const OUTLINE = 'inline-flex items-center gap-1 rounded border border-border px-2.5 py-1 text-[11px] font-medium hover:bg-muted disabled:opacity-60'
const PdfPagesPreview = lazy(() => import('@quotech/email-hub/feature/components/PdfPagesPreview'))
const QaItpSignaturePad = lazy(() => import('./QaItpSignaturePad'))
const QaItpSignSurface = lazy(() => import('../itp-sign/QaItpSignSurface').then((mod) => ({ default: mod.QaItpSignSurface })))

function SignedPreview({ bytes, desktop }: { bytes: Uint8Array | null; desktop: boolean }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!desktop || !bytes) {
      setUrl(null)
      return undefined
    }
    const buffer = new ArrayBuffer(bytes.byteLength)
    new Uint8Array(buffer).set(bytes)
    const next = URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }))
    setUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [bytes, desktop])
  if (!bytes) return null
  if (!desktop) return <PdfPagesPreview bytes={bytes} />
  if (!url) return null
  return <iframe title="Signed preview" className="h-full min-h-[70vh] w-full" src={url} />
}

export function QaItpExportView(props: {
  open: boolean
  onClose: () => void
  api: Pick<QaApi, 'renderItpPdf' | 'sendItpEmail' | 'listItpFromMailboxes'>
  inspectionId: string
  jobId: number
  orgId?: number
  executions: QaAreaExecution[]
  jobNumber: string | number
  request: QaItpRequest
  defaultShowAll: boolean
  reportTitle?: string
  cover?: ItpCoverFields
  frozenHtml?: string
  onSaveToLibrary?: (html: string) => void
}) {
  const model = useQaItpExport(props)
  const desktop = useIsMdUp()
  const [phoneTab, setPhoneTab] = useState<'document' | 'email'>('document')
  const [confirmSend, setConfirmSend] = useState<{ kind: 'signed' | 'unsigned'; name: string } | null>(null)
  const [holdSign, setHoldSign] = useState<{ itemId: string; areaId: string } | null>(null)
  const title = model.details.reportTitle || props.reportTitle || 'ITP'
  const { signing } = model
  const inline = useQaItpInlineSign(signing)
  const showSigned = signing.hasSignatures && signing.previewSigned
  const showSurface = inline.showSurface && signing.signBytes != null

  useEffect(() => {
    if (!props.open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || signing.busy) return
      event.stopPropagation()
      if ((model.dirty || signing.hasSignatures) && !window.confirm('Discard your changes to this ITP?')) return
      props.onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [props, signing.busy, signing.hasSignatures, model.dirty])

  if (!props.open || typeof document === 'undefined') return null

  const back = () => {
    if ((model.dirty || signing.hasSignatures) && !window.confirm('Discard your changes to this ITP?')) return
    props.onClose()
  }
  const sendTitle = model.readiness.blockers[0]
  const signLabel = signing.hasSignatures ? 'Re-sign' : 'Sign'
  const editMarks = () => {
    signing.setPreviewSigned(false)
    void signing.ensureSignBytes()
    inline.setShowSurface(true)
  }
  const actions = (
    <>
      <button type="button" className={desktop ? OUTLINE : 'inline-flex h-11 w-11 items-center justify-center'} aria-label="Print" onClick={model.print}>
        <Printer className="h-3.5 w-3.5" />{desktop ? 'Print' : null}
      </button>
      <button type="button" className={desktop ? OUTLINE : 'inline-flex h-11 w-11 items-center justify-center'} aria-label="Download PDF" disabled={model.downloading} onClick={() => { void model.download() }}>
        {model.downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        {desktop ? 'Download PDF' : null}
      </button>
      {props.onSaveToLibrary ? (
        <button type="button" className={desktop ? OUTLINE : 'inline-flex h-11 w-11 items-center justify-center'} aria-label="Save to ITP library" onClick={() => props.onSaveToLibrary?.(model.editedHtml)}>
          <Save className="h-3.5 w-3.5" />{desktop ? 'Library' : null}
        </button>
      ) : null}
      <button type="button" className={desktop ? OUTLINE : 'inline-flex h-11 w-11 items-center justify-center'} aria-label="Save to QDrive" disabled={model.savingCopy} onClick={() => { void model.saveToQDrive() }}>
        {model.savingCopy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FolderUp className="h-3.5 w-3.5" />}
        {desktop ? 'Save to QDrive' : null}
      </button>
      <QaItpSignMenu
        open={inline.menuOpen}
        label={signLabel}
        onToggle={() => inline.setMenuOpen(!inline.menuOpen)}
        onChoose={inline.choose}
      />
      <button
        type="button"
        className={desktop
          ? 'inline-flex items-center gap-1 rounded bg-primary px-2.5 py-1 text-[11px] font-bold text-primary-foreground disabled:opacity-60'
          : 'inline-flex min-h-[44px] items-center gap-1 rounded-md bg-primary px-3 text-xs font-semibold text-primary-foreground disabled:opacity-60'}
        disabled={model.sending || model.readiness.blockers.length > 0}
        title={sendTitle}
        onClick={() => inline.setCopyOpen(true)}
      >
        <Send className="h-3.5 w-3.5" /> Send
      </button>
    </>
  )
  const caption = !signing.hasSignatures ? (
    <p className="mt-2 flex-none text-center text-[10px] text-muted-foreground">
      Click anywhere in the document to edit. Print, Download PDF, Sign and Send use your edited version.
    </p>
  ) : showSigned ? (
    <p className="mt-2 flex-none text-center text-[10px] text-muted-foreground">
      Signed preview. Print, Download PDF and Send use this signed copy.
      <button type="button" className="ml-1 font-medium text-primary" onClick={() => { signing.setPreviewSigned(false); inline.setShowSurface(false) }}>
        Edit document
      </button>
      {signing.hasSignatures ? (
        <button type="button" className="ml-1 font-medium text-primary" onClick={editMarks}>
          Move or edit signature
        </button>
      ) : null}
    </p>
  ) : (
    <p className="mt-2 flex-none text-center text-[10px] text-muted-foreground">
      {`Signed on: ${signing.signedPagesLabel}. Your signature is added when you print, download or send.`}
      <button type="button" className="ml-1 font-medium text-primary" onClick={() => { void signing.showSignedPreview() }}>
        Show signed preview
      </button>
      <button type="button" className="ml-1 font-medium text-primary" onClick={editMarks}>
        Move or edit signature
      </button>
      <button type="button" className="ml-1 font-medium text-primary" onClick={signing.removeSignatures}>
        Remove signature
      </button>
    </p>
  )
  const phoneSignedLine = signing.hasSignatures ? (
    <p className="mb-2 flex-none text-center text-[10px] text-muted-foreground">
      {showSigned ? (
        <>
          Signed preview. Print, Download PDF and Send use this signed copy.
          <button type="button" className="ml-1 min-h-[44px] font-medium text-primary" onClick={() => { signing.setPreviewSigned(false); inline.setShowSurface(false) }}>
            Edit document
          </button>
          {signing.hasSignatures ? (
            <button type="button" className="ml-1 min-h-[44px] font-medium text-primary" onClick={editMarks}>
              Move or edit signature
            </button>
          ) : null}
        </>
      ) : (
        <>
          {`Signed on: ${signing.signedPagesLabel}. Your signature is added when you print, download or send.`}
          <button type="button" className="ml-1 min-h-[44px] font-medium text-primary" onClick={() => { void signing.showSignedPreview() }}>
            Show signed preview
          </button>
          <button type="button" className="ml-1 min-h-[44px] font-medium text-primary" onClick={editMarks}>
            Move or edit signature
          </button>
          <button type="button" className="ml-1 min-h-[44px] font-medium text-primary" onClick={signing.removeSignatures}>
            Remove signature
          </button>
        </>
      )}
    </p>
  ) : null
  const documentCard = (
    <div className={desktop
      ? 'mx-auto flex min-h-0 w-full max-w-[820px] flex-1 flex-col overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-border'
      : 'h-full overflow-hidden rounded-lg bg-white ring-1 ring-border'}
    >
      <div className={showSigned || showSurface ? 'hidden' : 'h-full'}>
        <QaItpExportDocument
          seedHtml={model.seedHtml}
          seedKey={model.seedKey}
          livePatch={model.livePatch}
          onChange={model.onDocumentChange}
          touch={!desktop}
          onRemoveCover={model.removeSavedCover}
          onHoldSign={(itemId, areaId) => setHoldSign({ itemId, areaId })}
        />
      </div>
      {showSurface && !showSigned && signing.signBytes ? (
        <Suspense fallback={<div className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}>
          <QaItpSignSurface
            bytes={signing.signBytes}
            items={signing.items}
            pendingIds={signing.pendingIds}
            onItemsChange={signing.replaceItems}
            onAccept={signing.acceptItem}
            onReject={signing.rejectItem}
            onSign={() => inline.draw('signature')}
            onInitials={() => inline.draw('initials')}
            onDownload={() => { void model.download() }}
            downloading={model.downloading}
            onPages={inline.onPages}
          />
        </Suspense>
      ) : null}
      {showSigned ? (
        <Suspense fallback={<div className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}>
          <SignedPreview bytes={signing.previewBytes} desktop={desktop} />
        </Suspense>
      ) : null}
    </div>
  )

  return createPortal(
    <div className="fixed inset-0 z-[60] flex flex-col overflow-hidden bg-muted pb-[env(safe-area-inset-bottom)]">
      {desktop ? (
        <header className="flex flex-none items-center justify-between border-b border-border bg-card px-3 py-2">
          <div className="flex min-w-0 items-center">
            <button type="button" onClick={back} className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to QA
            </button>
            <span className="mx-1 h-4 w-px bg-border" />
            <span className="truncate text-sm font-bold">{title}</span>
            <span className="ml-2 text-[12px] text-muted-foreground">Export</span>
          </div>
          <div className="flex items-center gap-1.5">{actions}</div>
        </header>
      ) : (
        <header className="flex flex-none items-center gap-1 border-b border-border bg-card px-1">
          <button type="button" aria-label="Back to QA" onClick={back} className="inline-flex h-11 w-11 items-center justify-center">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <span className="min-w-0 flex-1 truncate text-sm font-semibold">{title}</span>
          {actions}
        </header>
      )}
      {model.error || model.notice ? (
        <div className="flex flex-none items-center justify-between px-3 py-1 text-xs">
          {model.error ? (
            <p role="alert" className="text-destructive">
              {model.error}{' '}
              {/Reconnect .+ in Emails Hub/.test(model.error) ? (
                <a className="underline" href="/settings?tab=email">Open Emails Hub</a>
              ) : null}
              {model.largePermission?.authorizeUrl ? (
                <button
                  type="button"
                  className="ml-1 underline"
                  onClick={() => window.open(model.largePermission?.authorizeUrl, '_blank', 'noopener,noreferrer')}
                >
                  Allow larger attachments
                </button>
              ) : null}
            </p>
          ) : <p role="status" className="text-emerald-700">{model.notice}</p>}
          <button type="button" className="text-muted-foreground" onClick={() => { model.setError(null); model.setNotice(null) }}>Dismiss</button>
        </div>
      ) : null}
      {desktop ? (
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <aside className="w-[360px] flex-none overflow-y-auto border-r border-border bg-card p-4">
            <QaItpExportSide model={model} />
          </aside>
          <div className="flex min-w-0 flex-1 flex-col p-6">
            {documentCard}
            {caption}
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-none border-b border-border bg-card">
            <button type="button" className={`min-h-[44px] flex-1 text-xs font-semibold ${phoneTab === 'document' ? 'border-b-2 border-primary text-primary' : ''}`} onClick={() => setPhoneTab('document')}>Document</button>
            <button type="button" className={`min-h-[44px] flex-1 text-xs font-semibold ${phoneTab === 'email' ? 'border-b-2 border-primary text-primary' : ''}`} onClick={() => setPhoneTab('email')}>Email</button>
          </div>
          {phoneTab === 'document' ? (
            <div className="min-h-0 flex-1 p-2">
              {phoneSignedLine}
              {documentCard}
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              <QaItpExportSide model={model} />
            </div>
          )}
        </>
      )}
      <QaItpSizeDialog
        open={model.sizePrompt != null}
        sizeMb={model.sizePrompt?.sizeMb ?? 0}
        linkAvailable={model.sizePrompt?.linkAvailable ?? false}
        onCancel={model.dismissSize}
        onLink={() => { void model.send('link') }}
        onAttach={() => { void model.send('attach_anyway') }}
      />
      <QaItpSendCopyDialog
        open={inline.copyOpen}
        name={model.attachmentName}
        signedEnabled={signing.accepted || (!signing.inline && signing.hasSignatures)}
        onCancel={() => inline.setCopyOpen(false)}
        onSend={(kind, name) => {
          inline.setCopyOpen(false)
          setConfirmSend({ kind, name: attachmentNameForCopy(name, kind) })
        }}
      />
      <QaItpSendConfirmDialog
        open={confirmSend != null}
        to={model.to}
        subject={model.subject}
        message={model.body}
        from={model.fromLabel}
        cc={model.cc}
        bcc={model.bcc}
        onCancel={() => {
          setConfirmSend(null)
          if (!desktop) setPhoneTab('email')
        }}
        onSend={() => {
          const stored = confirmSend
          setConfirmSend(null)
          if (!stored) return
          void model.send('attach', stored)
        }}
      />
      {signing.padOpen ? (
        <Suspense fallback={<div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50"><Loader2 className="h-6 w-6 animate-spin text-white" /></div>}>
          <QaItpSignaturePad size={inline.padSize} onCancel={signing.closePad} onSaved={inline.onPadSaved} />
        </Suspense>
      ) : null}
      {holdSign ? (
        <QaItpHoldSignDialog
          itemId={holdSign.itemId}
          areaId={holdSign.areaId}
          jobId={props.jobId}
          api={props.api as Pick<QaApi, 'requestHoldPoint' | 'approveHoldPoint' | 'uploadFile'>}
          onClose={() => setHoldSign(null)}
        />
      ) : null}
    </div>,
    document.body,
  )
}
